import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { XMLElementBaseComponent } from '../base';
import { Subchapter } from '../volkswagen.types';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-xml-subchapter',
  imports: [TranslocoPipe, CommonModule],
  standalone: true,
  template: `
    <div class="my-4 rounded-lg border border-gray-300 bg-white p-4">
      <button class="cursor-pointer" tabindex="0" [attr.aria-expanded]="isExpanded" (click)="toggleExpanded()" (keydown)="onKeyDown($event)">
        <div class="mb-2 flex items-center justify-between gap-2">
          <div class="flex items-center">
            <svg
              class="mr-2 h-4 w-4 text-gray-600 transition-transform duration-200"
              [class.rotate-90]="isExpanded"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd" />
            </svg>
            <h3 class="text-left text-lg font-semibold text-gray-900">{{ getTitle() || ('documents.vw.subchapter' | transloco) }}</h3>
          </div>
          <span class="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 max-sm:hidden">
            {{ 'documents.vw.labels.id' | transloco }} {{ data.id }}
          </span>
        </div>
      </button>

      <div class="mb-2 text-xs text-gray-600">
        @if (data.children && data.children.length > 0) {
          {{ 'documents.vw.count' | transloco: { count: data.children.length } }}
        } @else {
          No content
        }
      </div>

      <div [ngClass]="{ hidden: !isExpanded }" class="print:block">
        <div class="mb-3 grid grid-cols-2 gap-2 border-t border-gray-200 pt-3 text-xs text-gray-700 md:grid-cols-4">
          <div class="overflow-x-auto">
            <span class="font-medium">{{ 'documents.vw.labels.type' | transloco }}</span>
            {{ data['typen-bez'] }}
          </div>
          <div class="overflow-x-auto">
            <span class="font-medium">{{ 'documents.vw.labels.salesType' | transloco }}</span>
            {{ data.verkaufstyp }}
          </div>
          <div class="overflow-x-auto">
            <span class="font-medium">{{ 'documents.vw.labels.language' | transloco }}</span>
            {{ data['{http://www.w3.org/XML/1998/namespace}lang'] }}
          </div>
        </div>

        <!-- Children will be rendered here -->
        <ng-template #childrenContainer class="space-y-3 border-t border-gray-200 pt-3"></ng-template>
      </div>
    </div>
  `,
})
export class SubchapterComponent extends XMLElementBaseComponent<Subchapter> implements OnInit {
  @ViewChild('childrenContainer', { read: ViewContainerRef })
  childrenContainer!: ViewContainerRef;
  isExpanded = false;

  cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this._renderChildren();
  }

  getTitle(): string {
    const titleChild = this.data?.children.find((child: any) => child.tag === 'titel') as { text: string };
    return titleChild?.text || '';
  }

  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleExpanded();
    }
  }

  private _renderChildren() {
    this.cdr.detectChanges();

    if (this.data?.children && this.childrenContainer) {
      this.childRenderService.setProvider(this.provider);
      this.childRenderService.renderChildren(this.data.children, this.childrenContainer);
    }
  }
}
